import type {
  StorageDetailResponse,
  StorageSummary,
} from "@/types/api";

export type StorageActionCode =
  | "ready"
  | "complete_local_setup"
  | "complete_smb_gateway_setup"
  | "complete_local_gateway_settings"
  | "configure_local_folder"
  | "approve_smb_relay"
  | "manual_publication"
  | "wait_auto_publication"
  | "wait_route"
  | "sync_failure"
  | "repair_credentials"
  | "update_gateway"
  | "payment_retry"
  | "rerun_registration"
  | "review_status";

type StorageActionSource = Pick<
  StorageSummary,
  | "settingBlockedReason"
  | "connectionKindId"
  | "settingState"
  | "requiredRecoveryAction"
> & {
  localGatewaySetupState?: StorageDetailResponse["localGatewaySetupState"];
  gatewayRouteSummary?: StorageDetailResponse["gatewayRouteSummary"];
  publicationSummary?: StorageDetailResponse["publicationSummary"] | null;
  operabilityWarnings?: string[];
};

export type StorageSettingDisplayState =
  | "ready"
  | "pending"
  | "blocked"
  | "unknown"
  | "disabled";

export type StorageRuntimeStatusLineCode =
  | "available"
  | "degraded"
  | "unavailable"
  | "unknown"
  | "not_observed"
  | "not_requested"
  | "fetch_failed";

export type StorageAvailabilityCode =
  | "available"
  | "setup_incomplete"
  | "needs_action"
  | "unavailable"
  | "needs_check";

export interface StorageProgressStep {
  id: "registration" | "gateway" | "root" | "publication" | "ready";
  state: "complete" | "current" | "upcoming";
  descriptionCode:
    | "registered"
    | "enter_token"
    | "gateway_linked"
    | "gateway_pending"
    | "local_root_confirmed"
    | "local_root_pending"
    | "manual_publication"
    | "wait_auto_publication"
    | "wait_route"
    | "review_status"
    | "publication_ready"
    | "ready"
    | "pending_ready";
}

type LocalStorageProgressSource = Pick<
  StorageDetailResponse,
  | "settingBlockedReason"
  | "settingState"
  | "requiredRecoveryAction"
  | "localGatewaySetupState"
  | "connectionKindId"
  | "publicationSummary"
  | "operabilityWarnings"
  | "gatewayRouteSummary"
>;

export function isLocalCapableStorage(
  storage: Pick<StorageSummary, "connectionKindId">,
) {
  return (
    storage.connectionKindId === "local_folder" ||
    storage.connectionKindId === "smb"
  );
}

export function getLocalGatewayRouteLimit(
  storage: Pick<StorageSummary, "connectionKindId">,
) {
  switch (storage.connectionKindId) {
    case "local_folder":
      return 1;
    case "smb":
      return 1;
    default:
      return null;
  }
}

export function resolveStorageRuntimeStatusLineCode(
  storage: Pick<StorageSummary, "runtimeStatusSnapshot">,
): StorageRuntimeStatusLineCode {
  const runtimeStatus = storage.runtimeStatusSnapshot;
  if (!runtimeStatus) return "not_requested";
  if (runtimeStatus.source === "pf_unavailable") {
    return "fetch_failed";
  }
  if (runtimeStatus.source === "not_requested") {
    return "not_requested";
  }
  if (
    runtimeStatus.source === "not_observed" ||
    runtimeStatus.freshnessState === "not_observed" ||
    runtimeStatus.runtimeStatusState === "not_requested"
  ) {
    return "not_observed";
  }
  if ((runtimeStatus.runtimeStatusState === "available" && runtimeStatus.freshnessState !== "fresh") ||
      (runtimeStatus.expiresAt !== null && Date.parse(runtimeStatus.expiresAt) <= Date.now())) {
    return "unknown";
  }
  return runtimeStatus.runtimeStatusState;
}

export function resolveStorageAvailabilityCode(
  settingState: StorageSettingDisplayState,
  runtimeState: StorageRuntimeStatusLineCode,
): StorageAvailabilityCode {
  if (settingState === "disabled") return "unavailable";
  if (settingState === "blocked") return "needs_action";
  if (settingState === "pending") return "setup_incomplete";
  if (settingState === "unknown") return "needs_check";
  if (runtimeState === "available") return "available";
  if (runtimeState === "unavailable") return "unavailable";
  return "needs_check";
}

export function resolveStorageIntegratedAvailabilityCode(
  storage: Pick<
    StorageSummary,
    "storageLifecycleState" | "settingState" | "runtimeStatusSnapshot"
  >,
): StorageAvailabilityCode {
  const settingState: StorageSettingDisplayState =
    storage.storageLifecycleState === "active"
      ? (storage.settingState as StorageSettingDisplayState)
      : "disabled";
  return resolveStorageAvailabilityCode(
    settingState,
    resolveStorageRuntimeStatusLineCode(storage),
  );
}

export function isStorageIntegratedAvailable(
  storage: Pick<
    StorageSummary,
    "storageLifecycleState" | "settingState" | "runtimeStatusSnapshot"
  >,
) {
  return resolveStorageIntegratedAvailabilityCode(storage) === "available";
}

export function resolveStorageActionCode(
  storage: StorageActionSource,
): StorageActionCode {
  const gatewayRouteSummary = storage.gatewayRouteSummary;
  const localGatewayRouteMissing =
    isLocalCapableStorage(storage) &&
    gatewayRouteSummary !== null &&
    gatewayRouteSummary !== undefined &&
    gatewayRouteSummary.activeRouteCount === 0;
  const hasActiveGatewayRoute =
    isLocalCapableStorage(storage) &&
    (gatewayRouteSummary?.activeRouteCount ?? 0) > 0;
  const localGatewayRouteSummaryInvalid =
    isLocalCapableStorage(storage) &&
    gatewayRouteSummary === null;
  if (storage.settingState === "ready" && !localGatewayRouteMissing) {
    return localGatewayRouteSummaryInvalid ? "review_status" : "ready";
  }
  if (
    storage.operabilityWarnings?.includes("platform_sync_failed") &&
    storage.requiredRecoveryAction !== "gateway_route_attach" &&
    storage.requiredRecoveryAction !== "gateway_route_repair"
  ) {
    return "sync_failure";
  }
  if (localGatewayRouteMissing) {
    return storage.connectionKindId === "smb"
      ? "complete_smb_gateway_setup"
      : "complete_local_setup";
  }

  if (
    storage.connectionKindId === "local_folder" &&
    hasActiveGatewayRoute &&
    storage.settingBlockedReason === "local_setup_incomplete" &&
    storage.localGatewaySetupState === "setup_pending" &&
    !storage.operabilityWarnings?.includes("platform_sync_failed")
  ) {
    return "configure_local_folder";
  }

  switch (storage.requiredRecoveryAction) {
    case "gateway_route_attach":
    case "gateway_route_repair":
      if (
        storage.connectionKindId === "smb" &&
        storage.settingBlockedReason === "local_setup_incomplete"
      ) {
        return "approve_smb_relay";
      }
      return storage.settingBlockedReason === "publication_not_ready"
        ? "manual_publication"
        : hasActiveGatewayRoute
          ? "complete_local_gateway_settings"
          : "complete_local_setup";
    case "customer_console_repair":
      return storage.settingBlockedReason === "credential_invalid"
        ? "repair_credentials"
        : "review_status";
    case "gateway_version_update":
      return "update_gateway";
    case "payment_retry":
      return "payment_retry";
    case "registration_rerun":
      return "rerun_registration";
    case "readiness_recheck":
      if (storage.settingBlockedReason === "publication_not_ready") {
        return storage.publicationSummary?.effectivePublishedOrigin
          ? "wait_route"
          : "wait_auto_publication";
      }
      return "review_status";
    default:
      return storage.settingBlockedReason ? "review_status" : "ready";
  }
}

export function resolveLocalStorageProgressSteps(
  storage: LocalStorageProgressSource,
): StorageProgressStep[] {
  const actionCode = resolveStorageActionCode(storage);
  const hasPlatformSyncFailure =
    storage.operabilityWarnings?.includes("platform_sync_failed") ?? false;
  const currentStateUnavailable =
    hasPlatformSyncFailure || storage.localGatewaySetupState === "not_observed";
  const registrationComplete =
    storage.settingBlockedReason !== "registration_incomplete";
  const hasActiveGatewayRoute =
    (storage.gatewayRouteSummary?.activeRouteCount ?? 0) > 0;
  const gatewayComplete =
    !currentStateUnavailable && hasActiveGatewayRoute;
  const rootComplete =
    !currentStateUnavailable &&
    hasActiveGatewayRoute &&
    (storage.localGatewaySetupState === "publication_pending" ||
      storage.localGatewaySetupState === "completed");
  const readyComplete = storage.settingState === "ready" && hasActiveGatewayRoute;
  const publicationComplete =
    readyComplete ||
    hasActiveGatewayRoute &&
    !hasPlatformSyncFailure &&
    Boolean(storage.publicationSummary?.effectivePublishedOrigin) &&
    actionCode !== "manual_publication" &&
    actionCode !== "wait_auto_publication" &&
    actionCode !== "wait_route";

  return [
    {
      id: "registration",
      state: registrationComplete ? "complete" : "current",
      descriptionCode: registrationComplete ? "registered" : "enter_token",
    },
    {
      id: "gateway",
      state: gatewayComplete
        ? "complete"
        : registrationComplete && !currentStateUnavailable
          ? "current"
          : "upcoming",
      descriptionCode: gatewayComplete ? "gateway_linked" : "gateway_pending",
    },
    {
      id: "root",
      state: rootComplete
        ? "complete"
        : gatewayComplete
          ? "current"
          : "upcoming",
      descriptionCode: rootComplete
        ? "local_root_confirmed"
        : "local_root_pending",
    },
    {
      id: "publication",
      state: publicationComplete
        ? "complete"
        : rootComplete
          ? "current"
          : "upcoming",
      descriptionCode:
        actionCode === "manual_publication"
          ? "manual_publication"
          : actionCode === "wait_route"
            ? "wait_route"
            : actionCode === "wait_auto_publication"
              ? "wait_auto_publication"
              : actionCode === "sync_failure"
                ? "review_status"
              : storage.settingState === "blocked"
                ? "review_status"
              : publicationComplete
                ? "publication_ready"
                : "wait_auto_publication",
    },
    {
      id: "ready",
      state: readyComplete
        ? "complete"
        : publicationComplete
          ? "current"
          : "upcoming",
      descriptionCode: readyComplete ? "ready" : "pending_ready",
    },
  ];
}
